import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgDesktop from '../images/background-desktop.jpg';
import bgMobile from '../images/background.png';

const isDesktop = () => window.matchMedia('(min-width: 600px)').matches;

function AuthIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'shield':    return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        case 'mail':      return <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
        case 'lock':      return <svg {...p}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
        case 'eye':       return <svg {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
        case 'eye-off':   return <svg {...p}><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
        case 'arrow-right': return <svg {...p}><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
        default: return null;
    }
}

export default function Login() {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [errors, setErrors] = useState({});
    const [ready, setReady] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [remember, setRemember] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const saved = localStorage.getItem('auth_remember_email');
        if (saved) {
            setFormData(f => ({ ...f, email: saved }));
            setRemember(true);
        }
        requestAnimationFrame(() => setReady(true));
    }, []);

    const handleLogin = (e) => {
        e.preventDefault();
        setErrors({});

        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setErrors(err => ({ ...err, email: t('errors.email_format') }));
            return;
        }

        setLoading(true);
        const data = new FormData();
        data.append('email', formData.email);
        data.append('password', formData.password);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${process.env.REACT_APP_API_URL}/login`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                setLoading(false);
                if (this.status === 200) {
                    const user = JSON.parse(this.responseText);
                    localStorage.setItem('userLogged', JSON.stringify({
                        username: user.username, profile: user.profile,
                        wines: user.wines, average: user.average,
                        id: user.id, email: user.email
                    }));
                    if (remember) {
                        localStorage.setItem('auth_remember_email', formData.email);
                    } else {
                        localStorage.removeItem('auth_remember_email');
                    }
                    navigate('/');
                } else {
                    setErrors(err => ({ ...err, general: t('errors.login') }));
                }
            }
        };
        xhr.send(data);
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(f => ({ ...f, [id]: value }));
    };

    const handleDemo = () => {
        localStorage.setItem('userLogged', JSON.stringify({
            id: 1, username: 'Marta Vela', average: 78.34,
            wines: 142, profile: 8, email: 'test@test.com', ranking: 12
        }));
        navigate('/');
    };

    const bg = isDesktop() ? bgDesktop : bgMobile;

    return (
        <div className={`auth-stage${ready ? ' auth-ready' : ''}`}>
            <div className="auth-bg" style={{ backgroundImage: `url(${bg})` }} />
            <div className="auth-vignette" />
            <div className="auth-grain" />

            <div className="auth-card">
                <div className="auth-brand">
                    <AuthIcon name="shield" size={14} />
                    <span className="auth-brand-name">{t('common.wine_police')}</span>
                </div>

                <h1 className="auth-heading">{t('login.title')}</h1>

                <form className="auth-form" onSubmit={handleLogin}>
                    <div className={`auth-field${errors.email ? ' auth-field-err' : ''}`}>
                        <input
                            className="auth-input"
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder=" "
                            autoComplete="email"
                        />
                        <label className="auth-label" htmlFor="email"><AuthIcon name="mail" size={11} />{t('common.email')}</label>
                    </div>
                    {errors.email && <p className="auth-error">{errors.email}</p>}

                    <div className={`auth-field auth-field-pass${errors.password ? ' auth-field-err' : ''}`}>
                        <input
                            className="auth-input"
                            type={showPass ? 'text' : 'password'}
                            id="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder=" "
                            autoComplete="current-password"
                        />
                        <label className="auth-label" htmlFor="password"><AuthIcon name="lock" size={11} />{t('login.label_password')}</label>
                        <button type="button" className="auth-eye" onClick={() => setShowPass(s => !s)} tabIndex={-1}>
                            <AuthIcon name={showPass ? 'eye-off' : 'eye'} size={14} />
                        </button>
                    </div>
                    {errors.password && <p className="auth-error">{errors.password}</p>}

                    {errors.general && (
                        <p className="auth-error auth-error-block">{errors.general}</p>
                    )}

                    <div className="auth-remember-wrap">
                        <input
                            type="checkbox"
                            id="remember"
                            className="auth-remember-check"
                            checked={remember}
                            onChange={e => setRemember(e.target.checked)}
                        />
                        <label htmlFor="remember" className="auth-remember-label">
                            {t('login.remember_email')}
                        </label>
                    </div>

                    <button className="auth-submit" type="submit" disabled={loading}>
                        {loading ? <span className="auth-spinner" /> : <>{t('common.send')}<AuthIcon name="arrow-right" size={15} /></>}
                    </button>
                </form>

                <div className="auth-links">
                    <button className="auth-link" onClick={() => navigate('/register')}>
                        {t('login.create_account')}
                    </button>
                    <button className="auth-link" onClick={() => navigate('/reset-pass')}>
                        {t('login.forgot_password')}
                    </button>
                </div>

                <button className="auth-demo" onClick={handleDemo}>Demo (sin backend)</button>
            </div>
        </div>
    );
}
